import React, { useRef, useState, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import FocusLock from 'react-focus-lock';
import useKeys from 'effects/keys';
import Exit from 'components/Icons/Exit/Exit';
import './Modal.scss';

const DEFAULT_ON_CLOSE = () => {};

const target = document.getElementById('modal');

interface ModalProps {
  className?: string | null;
  title?: string;
  preContent?: ReactNode;
  children?: ReactNode;
  postContent?: ReactNode;
  label?: string;
  close?: boolean;
  onClose?: () => void;
}

/**
 * This is the Modal component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Modal({
  className = null,
  title = '',
  preContent = null,
  children,
  postContent = null,
  label = 'Exit',
  close = false,
  onClose = DEFAULT_ON_CLOSE,
}: ModalProps) {
  const node = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setVisible(true);
    }, 120);

    return function cleanup() {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    document.body.classList.add('popup-open');

    return function cleanup() {
      document.body.classList.remove('popup-open');
    };
  }, [visible]);

  useKeys('submenu', visible, {
    esc: () => {
      setVisible(false);
    },
  });

  function renderCloseButton() {
    if (!close) return null;

    return (
      <button
        autoFocus
        className="close"
        onClick={() => {
          setVisible(false);
        }}
      >
        <Exit title={label} />
      </button>
    );
  }

  function render() {
    return (
      <FocusLock>
        <div
          ref={node}
          className={classnames('modal', className, { visible })}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onTransitionEnd={({ target }) => {
            if (target === node.current) {
              if (!visible) {
                onClose();
              }
            }
          }}
          onClick={({ target }) => {
            if (target === node.current && close) {
              setVisible(false);
            }
          }}
        >
          <div className="box">
            {preContent}
            <div>
              {renderCloseButton()}
              <div className="content">{children}</div>
            </div>
            {postContent}
          </div>
        </div>
      </FocusLock>
    );
  }

  if (target) {
    return createPortal(render(), target);
  }
  return render();
}

export default Modal;
